import { toValue, type MaybeRef } from "vue";

/**
 * Templating composable.
 *
 * Supported syntaxes:
 * 1. Triple brace JSON path tokens: {{{ user.constructs[0].score }}}
 *    - Dot notation + array index resolution against the injected report provider context.
 *    - If resolved value is an object, it is JSON.stringified with indentation.
 * 2. Answer DSL tokens: {{ assessment/demographic/question_1 }} or {{ some/path/uuid-question-id }}
 *    - Token is split on '/', last segment treated as answer id, preceding segments form a path.
 *    - Matches answers where (answer.data.path === path) AND (answer.data.id === id OR answer.data.binding.id === id).
 *    - Display value selection prioritizes: text -> option.name -> checkbox option names -> joined option names -> score -> raw value -> JSON.
 *
 * Typical usage in a component:
 *  const report = inject('report', null);
 *  const templateContext = computed(() => buildTemplateContext(report));
 *  const { applyTemplate, applyTemplateObject } = useTemplating(templateContext, { logger });
 *  const templated = computed(() => applyTemplateObject({
 *    title: definitionData.value?.title,
 *    subtitle: definitionData.value?.subtitle
 *  }));
 *
 * Provide {{{ ... }}} and {{ path/id }} in your block definition default strings or user-edited strings.
 */
export interface ReportProviderContext {
  user?: any;
  data?: any[]; // processed/mapped data
  kind?: string;
  reportFileName?: string;
  debug?: boolean;
  [key: string]: any;
}

export interface TemplatingOptions {
  /** When true, unresolved tokens are left intact instead of replaced */
  silent?: boolean;
  /** Custom logger (expects warn/debug methods) */
  logger?: { warn: (...args: any[]) => void; debug?: (...args: any[]) => void };
  /** Force-enable or disable debug placeholders */
  debug?: boolean;
}

function getValueByPath(obj: any, path: string): any {
  if (!obj || !path) return undefined;
  const keys = path.split('.');
  let current = obj;
  for (const key of keys) {
    if (current == null) return undefined;
    const arrayMatch = key.match(/^(\w+)\[(\d+)\]$/);
    if (arrayMatch) {
      const [, arrayKey, index] = arrayMatch;
      current = current[arrayKey]?.[parseInt(index, 10)];
    } else if (key.includes('[') && key.includes(']')) {
      const complexMatch = key.match(/^(\w+)\[(.+)\]$/);
      if (complexMatch) {
        const [, arrayKey, indexExpr] = complexMatch;
        const arrayValue = current[arrayKey];
        if (Array.isArray(arrayValue)) {
          const idx = parseInt(indexExpr, 10);
          current = arrayValue[idx];
        } else {
          current = undefined;
        }
      } else {
        current = current[key];
      }
    } else {
      current = current[key];
    }
  }
  return current;
}

interface AnswerItem {
  type: string;
  data: any;
}
interface IndexedAnswer {
  path: string;
  id: string;
  bindingId?: string;
  answer: AnswerItem;
}

function extractDisplayValue(a: AnswerItem): string {
  const dataObj = a.data?.data || {};
  const componentType: string | undefined = a.data?.type || a.data?.binding?.componentId;
  if (dataObj.text !== undefined) return String(dataObj.text);
  if (/radio-group|select/i.test(componentType || '')) {
    if (dataObj.option) return String(dataObj.option.name ?? '');
    const options = a.data?.binding?.options || [];
    const selected = options.find((o: any) => o.id === a.data?.value);
    if (selected) return String(selected.name ?? '');
  }
  if (/checkbox-group/i.test(componentType || '')) {
    if (Array.isArray(dataObj.options) && dataObj.options.length) {
      return dataObj.options.map((opt: any) => String(opt.name ?? '')).join(', ');
    }
    const rawIds: string[] = Array.isArray(a.data?.value) ? a.data.value : [];
    const options = a.data?.binding?.options || [];
    const matchedNames = options.filter((o: any) => rawIds.includes(o.id)).map((o: any) => String(o.name ?? ''));
    if (matchedNames.length) return matchedNames.join(', ');
  }
  if (dataObj.option && dataObj.option.name !== undefined) return String(dataObj.option.name);
  if (Array.isArray(dataObj.options) && dataObj.options.length) {
    const names = dataObj.options.map((opt: any) => opt.name ?? opt.value).filter((v: any) => v !== undefined);
    if (names.length) return names.join(', ');
  }
  if (dataObj.score !== undefined) return String(dataObj.score);
  const rawValue = a.data?.value;
  if (Array.isArray(rawValue)) return rawValue.join(', ');
  if (typeof rawValue === 'string' || typeof rawValue === 'number') return String(rawValue);
  try { return JSON.stringify(dataObj || rawValue); } catch { return ''; }
}

export function useTemplating(
  context: MaybeRef<ReportProviderContext | null | undefined>,
  options: TemplatingOptions = {}
) {
  function buildAnswerIndex(ctx: ReportProviderContext | null | undefined): IndexedAnswer[] {
    if (!ctx) return [];
    const answers: AnswerItem[] = (() => {
      const userAnswers = (ctx.user?.data || []) as AnswerItem[];
      if (Array.isArray(userAnswers) && userAnswers.length) return userAnswers;
      return (ctx.data || []) as AnswerItem[];
    })().filter(a => a && a.type === 'answer' && a.data);
    return answers.map(a => ({
      path: a.data.path || '',
      id: a.data.id,
      bindingId: a.data.binding?.id,
      answer: a
    }));
  }

  /**
   * Apply templating to a single string. If no context is available returns the original content.
   * @param content Raw string containing template tokens.
   */
  function applyTemplate(content: string | undefined | null): string {
    if (!content) return '';
    const ctx = toValue(context);
    if (!ctx) return content; // no context -> raw

    const index = buildAnswerIndex(ctx);
    const debugEnabled = options.debug ?? Boolean((ctx as any)?.debug);

    function parseLiteral(candidate: string): string | null {
      if (!candidate) return null;
      const first = candidate[0];
      const last = candidate[candidate.length - 1];
      if ((first === '"' && last === '"') || (first === "'" && last === "'") || (first === '`' && last === '`')) {
        return candidate.slice(1, -1);
      }
      return null;
    }

    function stringifyValue(value: any): string {
      if (typeof value === 'object') {
        try {
          return JSON.stringify(value, null, 2);
        } catch {
          return '';
        }
      }
      return String(value);
    }

    function resolvePathExpression(raw: string) {
      const parts = raw
        .split('||')
        .map(part => part.trim())
        .filter(part => part.length > 0);
      const primary = parts[0] ?? raw.trim();
      for (const candidate of parts) {
        const literal = parseLiteral(candidate);
        if (literal !== null) {
          return { found: true, value: literal, primary };
        }
        const value = getValueByPath(ctx, candidate);
        if (value !== undefined && value !== null) {
          return { found: true, value: stringifyValue(value), primary };
        }
      }
      return { found: false, value: '', primary };
    }

    function resolveAnswerExpression(raw: string) {
      const parts = raw
        .split('||')
        .map(part => part.trim())
        .filter(part => part.length > 0);
      const primary = parts[0] ?? raw.trim();
      for (const candidate of parts) {
        const literal = parseLiteral(candidate);
        if (literal !== null) {
          return { found: true, value: literal, primary };
        }
        if (candidate.includes('/')) {
          const answerParts = candidate.split('/').filter(Boolean);
          if (answerParts.length >= 2) {
            const answerId = answerParts.pop()!;
            const path = answerParts.join('/');
            const found = index.find(
              i => i.path === path && (i.id === answerId || i.bindingId === answerId)
            );
            if (found) {
              return { found: true, value: extractDisplayValue(found.answer), primary };
            }
          }
        }
        const value = getValueByPath(ctx, candidate);
        if (value !== undefined && value !== null) {
          return { found: true, value: stringifyValue(value), primary };
        }
      }
      return { found: false, value: '', primary };
    }

    // 1. Triple brace JSON paths {{{ path.to.value }}}
    let processed = content.replace(/\{\{\{\s*([^}]+)\s*\}\}\}/g, (match, path) => {
      const trimmedPath = path.trim();
      if (!trimmedPath) return '';
      const result = resolvePathExpression(trimmedPath);
      if (result.found) {
        return result.value;
      }
      options.logger?.warn?.('Template path not found', result.primary);
      if (options.silent) return match;
      return debugEnabled ? `[Path not found: ${result.primary}]` : '';
    });

    // 2. Answer DSL tokens {{ path/to/questionId }}
    processed = processed.replace(/\{\{\s*([^{}]+?)\s*\}\}/g, (match, token) => {
      if (match.startsWith('{{{')) return match; // ignore triple brace already processed
      const trimmed = token.trim();
      if (!trimmed.includes('/')) return match; // leave untouched if not DSL
      const result = resolveAnswerExpression(trimmed);
      if (result.found) {
        return result.value;
      }
      options.logger?.warn?.('Answer token not found', result.primary);
      if (options.silent) return match;
      return debugEnabled ? `[Answer not found: ${result.primary}]` : '';
    });

    return processed;
  }

  /**
   * Apply templating to all string properties in a shallow object (non‑recursive for nested objects).
   * Useful for block definitions with multiple string fields (title, subtitle, image, etc.).
   */
  function applyTemplateObject<T extends Record<string, any>>(obj: T): T {
    if (!obj) return obj;
    const clone: any = { ...obj };
    for (const key of Object.keys(clone)) {
      const val = clone[key];
      if (typeof val === 'string') clone[key] = applyTemplate(val);
    }
    return clone as T;
  }

  return { applyTemplate, applyTemplateObject };
}

/** Helper to build a combined context passed to useTemplating from a report provider injection */
/**
 * Safely build a template context from an injected report provider.
 * Returns null if not initialized so components can skip templating.
 */
export function buildTemplateContext(report: any): ReportProviderContext | null {
  if (!report?.context || !report.initialized) return null;
  return {
    ...report.context,
    processedData: report.data,
    kind: report.kind,
    reportFileName: report.fileName,
    debug: Boolean(report.debug)
  };
}
